import { useEffect, useRef } from 'react'
import CityStatusBarControl from '../components/MapboxGL/controls/CityStatusBarControl'
import TreeStatsBarControl from '../components/MapboxGL/controls/TreeStatsBarControl'
import BiomassLegendControl from '../components/MapboxGL/controls/BiomassLegendControl'

const getGeojsonMeta = mapData =>
  mapData?.metadata || mapData?.meta || mapData?.properties?.metadata || null

const numberFrom = (value, fallback = null) => {
  if (value === null || value === undefined || value === '') return fallback
  const number = Number(value)
  return Number.isFinite(number) ? number : fallback
}

const extractBiomassMetadata = mapData => {
  const meta = getGeojsonMeta(mapData)
  if (!meta) {
    return {
      totalBiomass: null,
      averageBiomass: null,
      treesWithBiomass: null,
    }
  }

  return {
    totalBiomass: numberFrom(meta.totalBiomass, null),
    averageBiomass: numberFrom(meta.averageBiomass, null),
    treesWithBiomass: numberFrom(meta.treesWithBiomass, null),
  }
}

const biomassSignature = metadata => [
  metadata.totalBiomass,
  metadata.averageBiomass,
  metadata.treesWithBiomass,
].map(value => value ?? '').join('|')

// Extraer estadísticas del GeoJSON
const extractStats = (mapData) => {
  const meta = mapData.meta
  const features = mapData.features || []
  if (meta) {
    return {
      treesCount: meta.totalTrees ?? features.length,
      speciesCount: meta.totalSpecies ?? 0,
      aliveTrees: meta.aliveTrees ?? 0,
      deadTrees: meta.deadTrees ?? 0,
      verifiedTrees: meta.verifiedTrees ?? 0,
    }
  }
  const speciesSet = new Set(features.map(f => f.properties?.tooltip?.specie).filter(Boolean))
  const aliveTrees = features.filter(f => f.properties?.alive).length
  return {
    treesCount: features.length,
    speciesCount: speciesSet.size,
    aliveTrees,
    deadTrees: features.length - aliveTrees,
    verifiedTrees: features.filter(f => f.properties?.treeVerified).length,
  }
}

/**
 * Hook para gestionar controles del mapa:
 *  - CityStatusBarControl: ciudad/cantón + tenant (centrado inferior)
 *  - TreeStatsBarControl: cantidad de árboles + especies (centrado superior)
 *  - BiomassLegendControl: metadatos de biomasa tipo leyenda
 *
 * @param {Object} map - Instancia del mapa de Mapbox
 * @param {boolean} mapLoaded - Indica si el mapa está cargado
 * @param {Object} options - Opciones de configuración
 */
export const useCityStatusBar = (map, mapLoaded, options = {}) => {
  const {
    cityName = 'Loja',
    citySlug = null,
    cityCenter,
    cityZoom,
    currentMapData,
    position = 'bottom-left',
    hideStats = false,
    disabled = false,
    appBarHeight = '0px',
    cities = [],
    cantones = [],
    countries = [],
    selectedCountry = null,
    currentTenants = [],
    selectedTenant = null,
    onCityChange = null,
    onTenantChange = null,
    onCountryChange = null,
    openCityDropdown = false,
    cityLabel = null,
    legendVisibility = {},
    showBiomassLegend = false,
  } = options
  const shouldShowBiomassLegend = Boolean(showBiomassLegend) && !hideStats
  const biomassLegendVisible = shouldShowBiomassLegend && legendVisibility.biomass !== false

  const cityBarRef = useRef(null)
  const statsBarRef = useRef(null)
  const biomassLegendRef = useRef(null)
  const controlAddedRef = useRef(false)
  const lastStatsRef = useRef(null)
  const lastBiomassRef = useRef(null)

  // FASE 1: Crear controles cuando el mapa esté listo y tengamos datos
  useEffect(() => {
    if (disabled || !map || !mapLoaded || controlAddedRef.current || !currentMapData) return

    const initialView = { center: cityCenter, zoom: cityZoom || 12 }
    const stats = extractStats(currentMapData)
    const biomassMetadata = extractBiomassMetadata(currentMapData)

    // Control de ciudad/tenant — top center (bajo el AppBar)
    const cityBar = new CityStatusBarControl(cityName, 'auto', {
      initialView,
      appBarHeight,
      cities,
      cantones,
      countries,
      selectedCountry,
      currentTenants,
      selectedTenant,
      currentCitySlug: citySlug,
      onCityChange,
      onTenantChange,
      onCountryChange,
      openCityDropdown,
      cityLabel,
    })

    // Control de estadísticas — bottom center
    const statsBar = !hideStats
      ? new TreeStatsBarControl({
          treesCount: stats.treesCount,
          speciesCount: stats.speciesCount,
        })
      : null

    // Leyenda de biomasa — top center, debajo de CityStatusBarControl
    const biomassLegend = shouldShowBiomassLegend
      ? new BiomassLegendControl({
          metadata: biomassMetadata,
          appBarHeight,
          visible: biomassLegendVisible,
        })
      : null

    cityBarRef.current = cityBar
    statsBarRef.current = statsBar
    biomassLegendRef.current = biomassLegend
    controlAddedRef.current = true
    lastStatsRef.current = stats
    lastBiomassRef.current = biomassSignature(biomassMetadata)

    // Ambos controles se montan sobre el contenedor del mapa directamente.
    // Se añaden con 'bottom-left' para satisfacer la API de Mapbox,
    // pero el posicionamiento real lo gestiona cada control internamente.
    map.addControl(cityBar, 'bottom-left')
    if (statsBar) map.addControl(statsBar, 'top-left')
    if (biomassLegend) map.addControl(biomassLegend, 'bottom-left')

    return () => {
      try { if (cityBarRef.current) map.removeControl(cityBarRef.current) } catch (e) { /* noop */ }
      try { if (statsBarRef.current) map.removeControl(statsBarRef.current) } catch (e) { /* noop */ }
      try { if (biomassLegendRef.current) map.removeControl(biomassLegendRef.current) } catch (e) { /* noop */ }
      cityBarRef.current = null
      statsBarRef.current = null
      biomassLegendRef.current = null
      controlAddedRef.current = false
      lastStatsRef.current = null
      lastBiomassRef.current = null
    }
    // Este efecto monta los controles una sola vez; los cambios de ciudad,
    // tenant y callbacks se sincronizan en los efectos específicos de abajo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, mapLoaded, position, currentMapData])

  // FASE 2: Actualizar estadísticas cuando cambian los datos
  useEffect(() => {
    if (!currentMapData || !controlAddedRef.current) return

    if (statsBarRef.current) {
      const stats = extractStats(currentMapData)
      const hasChanged =
        !lastStatsRef.current ||
        lastStatsRef.current.treesCount !== stats.treesCount ||
        lastStatsRef.current.speciesCount !== stats.speciesCount

      if (hasChanged) {
        if (currentMapData.meta) {
          statsBarRef.current.updateStatsFromMeta({
            totalTrees: stats.treesCount,
            totalSpecies: stats.speciesCount,
          })
        } else {
          statsBarRef.current.updateStats(stats.treesCount, stats.speciesCount)
        }
        lastStatsRef.current = stats
      }
    }

    if (biomassLegendRef.current) {
      const biomassMetadata = extractBiomassMetadata(currentMapData)
      const nextBiomassSignature = biomassSignature(biomassMetadata)
      if (lastBiomassRef.current !== nextBiomassSignature) {
        biomassLegendRef.current.updateMetadata(biomassMetadata)
        lastBiomassRef.current = nextBiomassSignature
      }
    }
  }, [currentMapData])

  // FASE 2.0: Crear o remover la leyenda de biomasa cuando cambia el permiso.
  useEffect(() => {
    if (!map || !mapLoaded || !controlAddedRef.current || !currentMapData) return

    if (shouldShowBiomassLegend && !biomassLegendRef.current) {
      const biomassMetadata = extractBiomassMetadata(currentMapData)
      const biomassLegend = new BiomassLegendControl({
        metadata: biomassMetadata,
        appBarHeight,
        visible: biomassLegendVisible,
      })

      biomassLegendRef.current = biomassLegend
      lastBiomassRef.current = biomassSignature(biomassMetadata)
      map.addControl(biomassLegend, 'bottom-left')
      return
    }

    if (!shouldShowBiomassLegend && biomassLegendRef.current) {
      try {
        map.removeControl(biomassLegendRef.current)
      } catch (e) {
        // noop
      }
      biomassLegendRef.current = null
      lastBiomassRef.current = null
    }
  }, [
    map,
    mapLoaded,
    currentMapData,
    appBarHeight,
    biomassLegendVisible,
    shouldShowBiomassLegend,
  ])

  // FASE 2.1: Sincronizar visibilidad de leyendas controladas desde el mapa
  useEffect(() => {
    if (!controlAddedRef.current) return
    biomassLegendRef.current?.setVisible?.(biomassLegendVisible)
  }, [biomassLegendVisible])

  // FASE 3: Actualizar ciudad
  useEffect(() => {
    if (!cityBarRef.current || !controlAddedRef.current) return
    cityBarRef.current.updateCity?.(cityName)
  }, [cityName])

  // FASE 4: Actualizar tenants
  useEffect(() => {
    if (!cityBarRef.current || !controlAddedRef.current) return
    cityBarRef.current.updateTenants?.(currentTenants, selectedTenant)
  }, [currentTenants, selectedTenant])

  // FASE 5: Actualizar lista de ciudades
  useEffect(() => {
    if (!cityBarRef.current || !controlAddedRef.current) return
    cityBarRef.current.updateCities?.(cities, citySlug)
  }, [cities, citySlug])

  // FASE 6: Actualizar callback de onTenantChange (evitar stale closures)
  useEffect(() => {
    if (!cityBarRef.current || !controlAddedRef.current) return
    cityBarRef.current.updateOnTenantChange?.(onTenantChange)
  }, [onTenantChange])

  // FASE 7: Actualizar callback de onCityChange (evitar stale closures)
  useEffect(() => {
    if (!cityBarRef.current || !controlAddedRef.current) return
    cityBarRef.current.updateOnCityChange?.(onCityChange)
  }, [onCityChange])

  // FASE 8: Actualizar países disponibles y país seleccionado
  useEffect(() => {
    if (!cityBarRef.current || !controlAddedRef.current) return
    cityBarRef.current.updateCountries?.(countries, selectedCountry)
  }, [countries, selectedCountry])

  // FASE 9: Actualizar callback de onCountryChange (evitar stale closures)
  useEffect(() => {
    if (!cityBarRef.current || !controlAddedRef.current) return
    cityBarRef.current.updateOnCountryChange?.(onCountryChange)
  }, [onCountryChange])

  // FASE 10: Actualizar estado de apertura automática del dropdown de ciudades
  useEffect(() => {
    if (!cityBarRef.current || !controlAddedRef.current) return
    cityBarRef.current.updateOpenCityDropdown?.(openCityDropdown)
  }, [openCityDropdown])

  // FASE 11: Actualizar etiqueta del dropdown de ciudades
  useEffect(() => {
    if (!cityBarRef.current || !controlAddedRef.current) return
    cityBarRef.current.updateCityLabel?.(cityLabel)
  }, [cityLabel])

  return {
    control: cityBarRef.current,
    statsControl: statsBarRef.current,
    biomassLegendControl: biomassLegendRef.current,
    updateStats: (treesCount, speciesCount) => {
      statsBarRef.current?.updateStats(treesCount, speciesCount)
    },
    updateStatsFromMeta: meta => {
      statsBarRef.current?.updateStatsFromMeta(meta)
    },
  }
}
